// Roles translations for React Admin resource
export const rolesResource = {
  name: 'Role |||| Roles',
  fields: {
    id: 'ID',
    name: 'Role Name',
    description: 'Description',
    permissions: 'Permissions',
    users: 'Users',
    createdAt: 'Created',
    updatedAt: 'Updated',
    userCount: 'Assigned Users',
  },
};

// Custom roles translations for components
export const roles = {
  subtitle: 'Manage user roles and their permissions',
  permissions: 'permission |||| permissions',
  selected: 'selected',
  system_role_info: 'This is a system role and cannot be deleted. You can still modify its permissions.',
  permissions_info: 'Select the permissions this role should have. Users with this role will be able to perform the selected actions.',
  permissions_warning: 'Changing permissions will affect all users with this role. Please review carefully before saving.',
  validation: {
    name_format: 'Role name can only contain letters, numbers, spaces, and hyphens',
  },
  helpers: {
    name: 'Choose a descriptive name for this role (e.g., "Site Manager", "Read Only")',
    description: 'Describe what this role is used for and who should have it',
  },
  sections: {
    basic_info: 'Basic Information',
    permissions: 'Permissions',
    role_info: 'Role Information',
  },
  actions: {
    save: 'Save Role',
  },
  create: {
    title: 'Create New Role',
    subtitle: 'Define a new role with specific permissions',
  },
  edit: {
    title: 'Edit Role',
    subtitle: 'Modify role details and permissions',
  },
  delete: {
    title: 'Delete Role',
    content: 'Are you sure you want to delete this role? This action cannot be undone.',
  },
  list: {
    title: 'Roles & Permissions',
    filters: {
      search: 'Search',
    },
    empty: {
      title: 'No roles found',
      subtitle: 'Create your first role to manage user permissions.',
    },
    users: 'user |||| users',
  },
  notifications: {
    created: 'Role created successfully',
    updated: 'Role updated successfully',
    deleted: 'Role deleted successfully',
    error: {
      create: 'Failed to create role',
      update: 'Failed to update role',
      delete: 'Failed to delete role',
      system_role: 'Cannot delete system role',
    },
  },
  groups: {
    users: 'Backend Users',
    roles: 'Roles & Permissions',
    sites: 'Charging Sites',
    'load-groups': 'Load Groups',
    chargers: 'Chargers',
    'charger-types': 'Charger Types',
    transactions: 'Transactions',
    reservations: 'Reservations',
    'reservation-policies': 'Reservation Policies',
    'user-groups': 'User Groups',
    app_users: 'App Users',
    wallet: 'Wallet',
    settings: 'System Settings',
    audit: 'Audit Logs',
    reports: 'Reports & Analytics',
    messages: 'In-App Messages',
    banners: 'Promotion Banners',
    promotions: 'Promotions & Coupons',
  },
  permissionGroups: {
    users: {
      label: 'Backend Users',
      permissions: {
        'users:read:all': {
          label: 'View all users',
          description: 'Can view all backend user accounts',
        },
        'users:create:all': {
          label: 'Create users',
          description: 'Can create new backend user accounts',
        },
        'users:update:all': {
          label: 'Edit users',
          description: 'Can modify existing backend user accounts',
        },
        'users:write:all': {
          label: 'Create and edit users (legacy)',
          description: 'Legacy umbrella permission. Prefer the granular create / edit permissions instead.',
        },
        'users:admin:all': {
          label: 'Delete users',
          description: 'Can delete user accounts and manage user settings',
        },
      },
    },
    roles: {
      label: 'Roles & Permissions',
      permissions: {
        'roles:read:all': {
          label: 'View roles',
          description: 'Can view all roles and their permissions',
        },
        'roles:write:all': {
          label: 'Manage roles',
          description: 'Can create and modify roles',
        },
        'roles:admin:all': {
          label: 'Delete roles',
          description: 'Can delete roles and manage role assignments',
        },
      },
    },
    sites: {
      label: 'Charging Sites',
      permissions: {
        'sites:read:all': {
          label: 'View all sites',
          description: 'Can view all charging site information',
        },
        'sites:write:all': {
          label: 'Manage sites',
          description: 'Can create and edit charging sites',
        },
        'sites:admin:all': {
          label: 'Delete sites',
          description: 'Can delete sites and manage site settings',
        },
      },
    },
    'load-groups': {
      label: 'Load Groups',
      permissions: {
        'load-groups:read:all': {
          label: 'View load groups',
          description: 'Can view power management groups',
        },
        'load-groups:write:all': {
          label: 'Manage load groups',
          description: 'Can create and edit load groups',
        },
        'load-groups:admin:all': {
          label: 'Delete load groups',
          description: 'Can delete load groups',
        },
      },
    },
    chargers: {
      label: 'Chargers',
      permissions: {
        'chargers:read:all': {
          label: 'View all chargers',
          description: 'Can view all charging stations',
        },
        'chargers:write:all': {
          label: 'Manage chargers',
          description: 'Can add and modify chargers',
        },
        'chargers:admin:all': {
          label: 'Delete chargers',
          description: 'Can remove chargers from the system',
        },
        'chargers:reboot': {
          label: 'Reboot chargers',
          description: 'Send OCPP reset command to remotely reboot chargers (Soft or Hard reset)',
        },
      },
    },
    'charger-types': {
      label: 'Charger Types',
      permissions: {
        'charger-types:read:all': {
          label: 'View charger types',
          description: 'Can view charger type configurations',
        },
        'charger-types:write:all': {
          label: 'Manage charger types',
          description: 'Can create and edit charger types',
        },
        'charger-types:admin:all': {
          label: 'Delete charger types',
          description: 'Can delete charger type definitions',
        },
      },
    },
    transactions: {
      label: 'Transactions',
      permissions: {
        'transactions:read:all': {
          label: 'View transactions',
          description: 'Can view all charging transactions',
        },
        'transactions:write:all': {
          label: 'Modify transactions',
          description: 'Can edit transaction records',
        },
        'transactions:admin:all': {
          label: 'Manage transactions',
          description: 'Can delete and refund transactions',
        },
      },
    },
    reservations: {
      label: 'Reservations',
      permissions: {
        'reservations:read:all': {
          label: 'View reservations',
          description: 'Can view all reservations',
        },
        'reservations:write:all': {
          label: 'Manage reservations',
          description: 'Can cancel, capture no-show fees, re-assign, and force-release wallet holds',
        },
        'reservations:admin:all': {
          label: 'Delete reservations',
          description: 'Can cancel any reservation',
        },
      },
    },
    'reservation-policies': {
      label: 'Reservation Policies',
      permissions: {
        'reservation-policies:read:all': {
          label: 'View reservation policies',
          description: 'Can view all reservation policies configured per site, charger, or load group',
        },
        'reservation-policies:write:all': {
          label: 'Manage reservation policies',
          description: 'Can create, edit, and delete reservation policies',
        },
      },
    },
    'user-groups': {
      label: 'User Groups',
      permissions: {
        'user-groups:read:all': {
          label: 'View user groups',
          description: 'Can view all user groups used as reservation eligibility primitives',
        },
        'user-groups:write:all': {
          label: 'Manage user groups',
          description: 'Can create, edit, delete user groups and manage group membership',
        },
      },
    },
    'app-users': {
      label: 'App Users',
      permissions: {
        'app-users:read:all': {
          label: 'View app users',
          description: 'Can view mobile app users',
        },
        'app-users:write:all': {
          label: 'Manage app users',
          description: 'Can edit app user accounts',
        },
        'app-users:admin:all': {
          label: 'Delete app users',
          description: 'Can delete app user accounts',
        },
      },
    },
    'app-account-deletion': {
      label: 'App Account Deletion',
      permissions: {
        'account_deletion_requests:process:all': {
          label: 'Process account deletion requests',
          description: 'View app account deletion requests and process them (anonymizes user data)',
        },
      },
    },
    'app-versions': {
      label: 'App Versions',
      permissions: {
        'app-versions:read:all': {
          label: 'View app versions',
          description: 'View the configured iOS and Android version metadata',
        },
        'app-versions:write:all': {
          label: 'Manage app versions',
          description: 'Edit iOS and Android version metadata, including force-update thresholds and store URLs',
        },
      },
    },
    wallet: {
      label: 'Wallet',
      permissions: {
        'wallet:read:all': {
          label: 'View wallets',
          description: 'Can view all user wallets',
        },
        'wallet:write:all': {
          label: 'Manage wallets',
          description: 'Can adjust wallet balances',
        },
        'wallet:admin:all': {
          label: 'Delete wallets',
          description: 'Can delete wallet records',
        },
      },
    },
    settings: {
      label: 'System Settings',
      permissions: {
        'settings:read:all': {
          label: 'View settings',
          description: 'Can view system configuration',
        },
        'settings:write:all': {
          label: 'Modify settings',
          description: 'Can change system settings',
        },
        'settings:admin:all': {
          label: 'Admin settings',
          description: 'Can manage critical settings',
        },
      },
    },
    audit: {
      label: 'Audit Logs',
      permissions: {
        'audit:read:all': {
          label: 'View audit logs',
          description: 'Can view system activity logs and user actions',
        },
        'audit:write:all': {
          label: 'Manage audit logs',
          description: 'Can export and manage audit log retention',
        },
        'audit:admin:all': {
          label: 'Admin audit logs',
          description: 'Can delete audit logs and manage audit settings',
        },
      },
    },
    reports: {
      label: 'Reports & Analytics',
      permissions: {
        'reports:revenue:read': {
          label: 'Revenue Analysis',
          description: 'Can view the revenue analysis report',
        },
        'reports:transactions:read': {
          label: 'Transaction Volume',
          description: 'Can view the transaction volume report',
        },
        'reports:sessions:read': {
          label: 'Session Analytics',
          description: 'Can view the session analytics report',
        },
        'reports:charger-utilization:read': {
          label: 'Charger Utilization',
          description: 'Can view the charger utilization report',
        },
        'reports:energy-consumption:read': {
          label: 'Energy Consumption',
          description: 'Can view the energy consumption report',
        },
        'reports:peak-usage:read': {
          label: 'Peak Usage Analysis',
          description: 'Can view the peak usage analysis report',
        },
        'reports:user-activity:read': {
          label: 'User Activity',
          description: 'Can view the user activity report',
        },
        'reports:vendor-performance:read': {
          label: 'Vendor Performance (admin only)',
          description: 'Can view the vendor performance cross-tenant comparison report',
        },
        'reports:site-comparison:read': {
          label: 'Site Comparison',
          description: 'Can view the site comparison report',
        },
        'reports:total-sessions:read': {
          label: 'Total Sessions',
          description: 'Can see total session counts (including sessions that delivered no energy) in reports and exports; otherwise only charged sessions are shown',
        },
        'reports:kiosk:read': {
          label: 'Kiosk',
          description: 'Can view and export the kiosk report: monthly pay-out, daily match, refunds health and overview',
        },
      },
    },
    notifications: {
      label: 'Notifications',
      permissions: {
        'notifications:read:all': {
          label: 'View notifications',
          description: 'Can view notification groups, assignments, and logs',
        },
        'notifications:write:all': {
          label: 'Manage notifications',
          description: 'Can create and edit notification groups, add/remove members',
        },
        'notifications:manage:all': {
          label: 'Full notification control',
          description: 'Can delete groups, manage preferences, and retry failed notifications',
        },
      },
    },
    'email-routing': {
      label: 'Email Provider Routing',
      permissions: {
        'email_routing:read:all': {
          label: 'View email routing',
          description: 'Can view per-template email provider routing and the default provider',
        },
        'email_routing:write:all': {
          label: 'Manage email routing',
          description: 'Can change per-template email provider, default provider, and enable/disable templates',
        },
      },
    },
    messages: {
      label: 'In-App Messages',
      permissions: {
        'messages:read:all': {
          label: 'View messages',
          description: 'Can view all in-app messages and their statistics',
        },
        'messages:write:all': {
          label: 'Create and edit messages',
          description: 'Can create new messages and edit draft or scheduled messages',
        },
        'messages:send:all': {
          label: 'Send messages',
          description: 'Can send messages and push notifications to users',
        },
        'messages:admin:all': {
          label: 'Full message control',
          description: 'Can delete messages and manage all message settings',
        },
      },
    },
    banners: {
      label: 'Promotion Banners',
      permissions: {
        'banners:read:all': {
          label: 'View banners',
          description: 'Can view all promotional banners and their details',
        },
        'banners:write:all': {
          label: 'Create and edit banners',
          description: 'Can create new banners and edit existing promotional content',
        },
        'banners:analytics:all': {
          label: 'View banner analytics',
          description: 'Can access banner performance metrics and analytics data',
        },
        'banners:admin:all': {
          label: 'Full banner control',
          description: 'Can delete banners, manage targeting rules, and control all banner settings',
        },
      },
    },
    promotions: {
      label: 'Promotions & Coupons',
      permissions: {
        'promotions:read:all': {
          label: 'View promotions',
          description: 'Can view all promotions, coupons, and redemption history',
        },
        'promotions:write:all': {
          label: 'Manage promotions',
          description: 'Can create, edit, and generate coupon codes for promotions',
        },
        'promotions:export:all': {
          label: 'Export promotion data',
          description: 'Can export promotion codes and redemption reports to CSV',
        },
        'promotions:admin:all': {
          label: 'Full promotion control',
          description: 'Can delete promotions, view analytics, and manage all promotion settings',
        },
      },
    },
    'ocpp-servers': {
      label: 'OCPP Servers',
      permissions: {
        'ocpp-servers:read:all': {
          label: 'View OCPP servers',
          description: 'Can view OCPP server configurations and status',
        },
        'ocpp-servers:write:all': {
          label: 'Manage OCPP servers',
          description: 'Can create and edit OCPP server configurations',
        },
        'ocpp-servers:admin:all': {
          label: 'Delete OCPP servers',
          description: 'Can delete OCPP servers and manage server credentials',
        },
      },
    },
    'ocpp-templates': {
      label: 'OCPP Templates',
      permissions: {
        'ocpp-templates:read:all': {
          label: 'View OCPP templates',
          description: 'Can view all OCPP configuration templates',
        },
        'ocpp-templates:write:all': {
          label: 'Manage OCPP templates',
          description: 'Can create and edit OCPP configuration templates',
        },
        'ocpp-templates:admin:all': {
          label: 'Delete OCPP templates',
          description: 'Can delete OCPP templates and set default configurations',
        },
      },
    },
    'supabase-connections': {
      label: 'Supabase Connections',
      permissions: {
        'supabase-connections:read:all': {
          label: 'View Supabase connections',
          description: 'Can view Supabase connection configurations and status',
        },
        'supabase-connections:write:all': {
          label: 'Manage Supabase connections',
          description: 'Can create, edit, and test Supabase connections',
        },
        'supabase-connections:admin:all': {
          label: 'Delete Supabase connections',
          description: 'Can delete connections and manage encrypted credentials',
        },
      },
    },
    'lms-logs': {
      label: 'LMS Logs',
      permissions: {
        'lms-logs:read:all': {
          label: 'View LMS logs',
          description: 'Can view Load Management System decision logs and history',
        },
        'lms-logs:write:all': {
          label: 'Export LMS logs',
          description: 'Can export and download LMS log data',
        },
        'lms-logs:admin:all': {
          label: 'Manage LMS logs',
          description: 'Can delete LMS logs and configure retention policies',
        },
      },
    },
    'ocpp-logs': {
      label: 'OCPP Logs',
      permissions: {
        'ocpp-logs:read:all': {
          label: 'View OCPP logs',
          description: 'Can query and inspect raw OCPP protocol messages by charger and time range',
        },
      },
    },
    'lms-templates': {
      label: 'LMS Templates',
      permissions: {
        'lms-templates:read:all': {
          label: 'View LMS templates',
          description: 'Can view all load management system templates and configurations',
        },
        'lms-templates:write:all': {
          label: 'Manage LMS templates',
          description: 'Can create and edit load management system templates',
        },
        'lms-templates:admin:all': {
          label: 'Delete LMS templates',
          description: 'Can delete LMS templates and manage template settings',
        },
      },
    },
    'kiosk-transactions': {
      label: 'Kiosk Transactions',
      permissions: {
        'kiosk-transactions:read:all': {
          label: 'Read Kiosk Transactions',
          description: 'View and export kiosk payments and their refunds, and search by Octopus number',
        },
        'kiosk-transactions:write:all': {
          label: 'Write Kiosk Transactions',
          description: 'Mark an expired Octopus refund refunded by hand',
        },
      },
    },
    'kiosk-settlements': {
      label: 'Octopus settlements',
      permissions: {
        'kiosk-settlements:read:all': {
          label: 'Read Octopus settlements',
          description: 'View and export each Octopus reader settlement beside our record, and see overdue settlements',
        },
      },
    },
    'kiosk-alerts': {
      label: 'Kiosk alerts',
      permissions: {
        'kiosk-alerts:read:all': {
          label: 'Read kiosk alerts',
          description: 'See kiosk alerts and their counts. Site owners see only the day-to-day alerts of their own sites',
        },
        'kiosk-alerts:write:all': {
          label: 'Write kiosk alerts',
          description: 'Mark a kiosk alert handled with a note',
        },
      },
    },
    'kiosk-releases': {
      label: 'Kiosk releases',
      permissions: {
        'kiosk-releases:read:all': {
          label: 'Read kiosk releases',
          description: 'View kiosk software releases, rollouts and install progress',
        },
        'kiosk-releases:write:all': {
          label: 'Write kiosk releases',
          description: 'Upload, roll out, pause and revoke kiosk software releases',
        },
      },
    },
    'kiosk-release-keys': {
      label: 'Kiosk release keys',
      permissions: {
        'kiosk-release-keys:write:all': {
          label: 'Write kiosk release keys',
          description: 'Create, rotate and replace the kiosk release key',
        },
      },
    },
    'kiosk-devices': {
      label: 'Kiosks',
      permissions: {
        'kiosk-devices:read:all': {
          label: 'Read Kiosks',
          description: 'View kiosks, their health and settings',
        },
        'kiosk-devices:write:all': {
          label: 'Write Kiosks',
          description: 'Register, enrol, configure and disable kiosks',
        },
      },
    },
    vendors: {
      label: 'Vendors',
      permissions: {
        'vendors:read:all': {
          label: 'View vendors',
          description: 'Can view all vendor accounts and their details',
        },
        'vendors:write:all': {
          label: 'Manage vendors',
          description: 'Can create and edit vendor accounts and site assignments',
        },
        'vendors:admin:all': {
          label: 'Delete vendors',
          description: 'Can delete vendor accounts and manage vendor settings',
        },
      },
    },
    'rfid-users': {
      label: 'RFID Users',
      permissions: {
        'rfid-users:read:all': {
          label: 'View RFID users',
          description: 'Can view all RFID-only user accounts',
        },
        'rfid-users:write:all': {
          label: 'Manage RFID users',
          description: 'Can create and edit RFID user accounts',
        },
        'rfid-users:admin:all': {
          label: 'Delete RFID users',
          description: 'Can delete RFID user accounts and manage user cards',
        },
      },
    },
    'rfid-cards': {
      label: 'RFID Cards',
      permissions: {
        'rfid-cards:read:all': {
          label: 'View RFID cards',
          description: 'Can view all RFID cards and their assignments',
        },
        'rfid-cards:write:all': {
          label: 'Manage RFID cards',
          description: 'Can create, edit, and assign RFID cards to sites',
        },
        'rfid-cards:admin:all': {
          label: 'Delete RFID cards',
          description: 'Can delete cards, bulk import/export, and manage card settings',
        },
      },
    },
    'app-transactions': {
      label: 'App Transactions',
      permissions: {
        'app-transactions:read:all': {
          label: 'View app transactions',
          description: 'Can view all app payment transactions and details',
        },
        'app-transactions:write:all': {
          label: 'Manage app transactions',
          description: 'Can create and edit app payment transaction records',
        },
        'app-transactions:export:all': {
          label: 'Export app transactions',
          description: 'Can export app transaction data and generate reports',
        },
        'app-transactions:admin:all': {
          label: 'Admin app transactions',
          description: 'Full control over app transactions including refunds and adjustments',
        },
      },
    },
    'app-topups': {
      label: 'App Top-Ups',
      permissions: {
        'app-topups:read:all': {
          label: 'View top-ups',
          description: 'Can view wallet top-up transactions and user wallet balances',
        },
        'app-topups:export:all': {
          label: 'Export top-ups',
          description: 'Can export top-up transaction data to CSV',
        },
        'app-topups:admin:all': {
          label: 'Admin top-ups',
          description: 'Full control over top-ups including refunds',
        },
      },
    },
    'rfid-transactions': {
      label: 'RFID Transactions',
      permissions: {
        'rfid-transactions:read:all': {
          label: 'View RFID transactions',
          description: 'Can view all RFID payment transactions and details',
        },
        'rfid-transactions:write:all': {
          label: 'Manage RFID transactions',
          description: 'Can create and edit RFID payment transaction records',
        },
        'rfid-transactions:export:all': {
          label: 'Export RFID transactions',
          description: 'Can export RFID transaction data and generate reports',
        },
        'rfid-transactions:admin:all': {
          label: 'Admin RFID transactions',
          description: 'Full control over RFID transactions including refunds and adjustments',
        },
      },
    },
    'active-sessions': {
      label: 'Active Sessions',
      permissions: {
        'active-sessions:read:all': {
          label: 'View active sessions',
          description: 'Can view and monitor all active charging sessions',
        },
        'charging_sessions:manual_start': {
          label: 'Manual start sessions',
          description: 'Manually start charging sessions on behalf of users or standalone for testing/maintenance',
        },
        'charging_sessions:manual_stop': {
          label: 'Manual stop sessions',
          description: 'Manually stop any active charging session with reason documentation',
        },
        'charging_sessions:force_complete': {
          label: 'Force complete sessions',
          description: 'Manually force-complete stuck sessions when charger is offline or unreachable (network outage, hardware failure)',
        },
      },
    },
    'charging-sessions': {
      label: 'Charging History',
      permissions: {
        'charging-sessions:read:all': {
          label: 'View charging history',
          description: 'Can view all archived charging sessions and their details',
        },
        'charging-sessions:export:all': {
          label: 'Export charging history',
          description: 'Can export charging history (per-transaction usage report) to Excel',
        },
        'charging-sessions:view-analytics:all': {
          label: 'View Analytics tab',
          description: 'Can view the Analytics tab (meter-value charts) on a charging-session detail page',
        },
        'charging-sessions:view-payment:all': {
          label: 'View Payment tab',
          description: 'Can view the Payment tab (cost breakdown and payment details) on a charging-session detail page',
        },
        'charging-sessions:view-ocpp:all': {
          label: 'View OCPP logs',
          description: 'Can view the OCPP Logs tab and the "View OCPP Logs" button. Required to call the per-session OCPP logs API.',
        },
        'charging-sessions:view-technical:all': {
          label: 'View Technical tab',
          description: 'Can view the Technical tab (temperature, error counts, archive metadata) on a charging-session detail page',
        },
      },
    },
    'charger-firmware': {
      label: 'Charger Firmware',
      permissions: {
        'charger-firmware:read:all': {
          label: 'View firmware files',
          description: 'Can view all firmware files and their metadata',
        },
        'charger-firmware:write:all': {
          label: 'Upload firmware files',
          description: 'Can upload new firmware files and edit firmware metadata',
        },
        'charger-firmware:admin:all': {
          label: 'Delete firmware files',
          description: 'Can delete firmware files from the system',
        },
      },
    },
    'firmware-updates': {
      label: 'Firmware Updates',
      permissions: {
        'firmware-updates:read:all': {
          label: 'View update jobs',
          description: 'Can view all firmware update jobs and their status',
        },
        'firmware-updates:write:all': {
          label: 'Create update jobs',
          description: 'Can create firmware update jobs and schedule charger updates',
        },
        'firmware-updates:admin:all': {
          label: 'Manage update jobs',
          description: 'Can cancel firmware update jobs and query status',
        },
      },
    },
    'fee-templates': {
      label: 'Fee Templates',
      permissions: {
        'fee-templates:read:all': {
          label: 'View fee templates',
          description: 'Can view all fee templates and their configurations',
        },
        'fee-templates:write:all': {
          label: 'Create and edit fee templates',
          description: 'Can create new fee templates and edit existing ones',
        },
        'fee-templates:admin:all': {
          label: 'Delete fee templates',
          description: 'Can delete fee templates and manage template settings',
        },
      },
    },
    'fee-schedules': {
      label: 'Fee Schedules',
      permissions: {
        'fee-schedules:read:all': {
          label: 'View fee schedules',
          description: 'Can view all fee schedules and their time-based pricing rules',
        },
        'fee-schedules:write:all': {
          label: 'Create and edit fee schedules',
          description: 'Can create new fee schedules and edit existing ones',
        },
        'fee-schedules:admin:all': {
          label: 'Delete fee schedules',
          description: 'Can delete fee schedules and manage schedule settings',
        },
      },
    },
    'payment-settings': {
      label: 'Payment Settings',
      permissions: {
        'payment-settings:read:all': {
          label: 'View payment settings',
          description: 'Can view payment gateways and payment methods configuration',
        },
        'payment-settings:write:all': {
          label: 'Manage payment settings',
          description: 'Can edit payment gateway settings, enable/disable payment methods, and reorder payment methods',
        },
      },
    },
    'vehicle-catalog': {
      label: 'Vehicle Catalog',
      permissions: {
        'vehicle-catalog:read:all': {
          label: 'Read Vehicle Catalog',
          description: 'View vehicle brands and models',
        },
        'vehicle-catalog:write:all': {
          label: 'Write Vehicle Catalog',
          description: 'Create, edit, and delete vehicle brands and models',
        },
        'vehicle-catalog:import:all': {
          label: 'Import Vehicle Catalog',
          description: 'Bulk import vehicle models from PDF',
        },
      },
    },
    'ai-troubleshooting': {
      label: 'AI Troubleshooting',
      permissions: {
        'ai_troubleshooting:use:all': {
          label: 'Use AI Troubleshooting',
          description: 'Run AI troubleshooting investigations, view in-scope investigation history and detail, and copy or download the developer debug-handoff package',
        },
        'ai_troubleshooting:manage:all': {
          label: 'Manage AI Troubleshooting',
          description: 'Promote investigations into playbooks, manage playbooks and model configurations, view recurring-fault analytics, and run model A/B comparisons (Phase 2)',
        },
      },
    },
  },
};